// Dependencies
import React, { useState, useEffect, useCallback, useRef } from "react";
import { createStorefrontApiClient } from '@shopify/storefront-api-client';
// Hooks
import useLocalStorage from "../hooks/useLocalStorage";
import { useLocalStorageV2 } from "../hooks/useLocalStorageV2";
// Queries
import {
  GRAPHQL_PRODUCT_FIELDS,
  GRAPHQL_PRODUCT_VARIANTS,
  GRAPHQL_CART_FIELDS
} from '../queries/shopify';
// Debug
import { debugLog, debugError, debugWarn } from '../helpers/debug';

const CART_ID_KEY = 'tm_cart_id_v2'

export const shopifyClient = {
  'EUR': createStorefrontApiClient({
    storeDomain: process.env.GATSBY_SHOPIFY_STORE_URL_EUR,
    apiVersion: process.env.GATSBY_SHOPIFY_API_VERSION || '2025-01',
    publicAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN_EUR
  }),
  'ZAR': createStorefrontApiClient({
    storeDomain: process.env.GATSBY_SHOPIFY_STORE_URL_ZAR,
    apiVersion: process.env.GATSBY_SHOPIFY_API_VERSION || '2025-01',
    publicAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN_ZAR
  })
}

export const CartContext = React.createContext();
export const CartActionsContext = React.createContext();

export function useCartContext() {
	return React.useContext(CartContext);
}

export function useCartActions() {
	return React.useContext(CartActionsContext);
}

// Async function to fetch cart by ID
async function fetchCartById(id, currency) {
  if (!id) return null

  const query = `
    {
      cart (
        id: "${id}"
      ) {
        ${GRAPHQL_CART_FIELDS}
      }
    }
  `
  
  try {
    const { data, errors } = await shopifyClient[currency].request(query)

    if (errors) {
      debugError('cart response error', errors)
      return null
    } else {
      debugLog('fetchCartById data', data)
      return data
    }
  } catch (error) {
    debugError('fetchCartById error', error)
    return null
  }
}

// Async function to create new cart
async function createCart(currency) {
  const operation = `mutation createCart {
    cartCreate {
      cart {
        ${GRAPHQL_CART_FIELDS}
      }
    }
  }`

  try {
    const { data, errors } = await shopifyClient[currency].request(operation)

    if (errors) {
      debugError('createCart error', errors)
      return null
    } else {
      debugLog('createCart data', data)
      return data.cartCreate.cart
    }
  } catch (error) {
    debugError('createCart error', error)
    return null
  }
}

const CheckoutStore = ({ children }) => {

  // ----------------------------
  // Which currency?
  const [currency, setCurrency] = useLocalStorage('tm_currency_v3', 'ZAR'); // 'EUR' or 'ZAR'

  // Cart state - updated to new API structure
  const localStorageCart = useLocalStorageV2(CART_ID_KEY, null)
  const cartInitialized = localStorageCart?.initialized
  const cartId = localStorageCart?.value
  
  const [cart, setCart] = useState(null)
  const [loading, setLoading] = useState(false)

  debugLog('CheckoutStore initialized', {
    cartInitialized,
    cartId,
    currency,
    hasLocalStorageCart: !!localStorageCart
  })

  const createNewCart = useCallback(async () => {
    debugLog('Creating new cart', { currency })
    const newCart = await createCart(currency)
    if (newCart) {
      debugLog('Setting new cart', { cartId: newCart.id })
      localStorageCart.setValue(newCart.id)
      setCart(newCart)
    }
  }, [currency, localStorageCart])

  // Initialize cart
  const fetchCart = useCallback(async () => {
    debugLog('fetchCart function called', {
      cartInitialized,
      hasCart: !!cart,
      cartId,
      currency
    })

    if (cartInitialized && !cart) {
      if (cartId) {
        debugLog('Attempting to fetch existing cart', { cartId, currency })
        setLoading(true)
        
        try {
          const existingCart = await fetchCartById(cartId, currency)
          if (existingCart && existingCart.cart) {
            debugLog('Successfully fetched existing cart')
            setCart(existingCart.cart)
          } else {
            debugWarn('No existing cart found, creating new cart')
            await createNewCart()
          }
        } catch (error) {
          debugError('Error fetching cart, creating new one', error)
          await createNewCart()
        } finally {
          setLoading(false)
        }
      } else {
        debugLog('No cart ID found, creating new cart')
        await createNewCart()
      }
    }
  }, [cartInitialized, cartId, currency, createNewCart])

  // Initialize cart on mount and when currency changes
  useEffect(() => {
    fetchCart()
  }, [cartInitialized, cartId, currency]) // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------
  // When currency updates, reinitialize cart
  const firstRender = useRef(true);
  const updateCartCurrency = useCallback(() => {
    if (!firstRender.current) {
      debugLog('Currency changed, creating new cart', { currency })
      // Clear current cart and create new one for new currency
      setCart(null)
      localStorageCart.removeValue()
      createNewCart()
    } else {
      firstRender.current = false
    }
  }, [currency, localStorageCart, createNewCart])
  
  useEffect(() => {
    updateCartCurrency()
  }, [currency]) // eslint-disable-line react-hooks/exhaustive-deps

	// ----------------------------
	// Fetch live products as a collection to reconcile collection pages
	const [liveProducts, setLiveProducts] = useState([])
	
	const fetchProducts = useCallback(async (currency) => {
		debugLog('Fetching products for currency', { currency })
		
		const query = `
			query getProducts($first: Int!) {
				products(first: $first) {
					nodes {
						${GRAPHQL_PRODUCT_FIELDS}
						${GRAPHQL_PRODUCT_VARIANTS}
					}
				}
			}
		`
		
		try {
			const { data, errors } = await shopifyClient[currency].request(query, {
				variables: { first: 250 }
			})
			
			if (errors) {
				debugError('fetchProducts GraphQL errors', errors)
				return []
			}
			
			if (data?.products?.nodes) {
				debugLog(`Fetched ${data.products.nodes.length} products for ${currency}`)
				// Transform the data to match expected format
				const transformedProducts = data.products.nodes.map(product => ({
					...product,
					variants: product.variants.nodes.map(variant => ({
						...variant,
						available: variant.availableForSale,
						priceV2: variant.price
					}))
				}))
				return transformedProducts
			}
			
			return []
		} catch (error) {
			debugError('fetchProducts error', error)
			return []
		}
	}, [])
	
	useEffect(() => {
		if (currency) {
			fetchProducts(currency).then(products => {
				setLiveProducts(products)
			})
		}
	}, [currency]) // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------
  // Add item to cart

	const [didJustAddToCart, setDidJustAddToCart] = useState(null) // takes a variant id
	useEffect(() => {
		if (didJustAddToCart) {
			const timer = setTimeout(() => {
				setDidJustAddToCart(null)
			}, 1000);
			return () => clearTimeout(timer);
		}
	}, [didJustAddToCart])

  const addToCart = async (lineItemsToAdd, callback) => {
    if (!cart || !cart.id) {
      debugError('No cart available for adding items')
      return
    }

    debugLog('Adding items to cart', { lineItemsToAdd, cartId: cart.id })
    setLoading(true)

    const operation = `
      mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart {
            ${GRAPHQL_CART_FIELDS}
          }
          userErrors {
            field
            message
          }
        }
      }
    `
    
    try {
      const { data, errors } = await shopifyClient[currency].request(operation, {
        variables: {
          lines: lineItemsToAdd,
          cartId: cart.id
        }
      })

      if (errors) {
        debugError('addToCart GraphQL errors', errors)
        callback && callback(errors)
        return
      }

      if (data?.cartLinesAdd?.userErrors?.length > 0) {
        debugError('addToCart userErrors', data.cartLinesAdd.userErrors)
        callback && callback(data.cartLinesAdd.userErrors)
        return
      }

      if (data?.cartLinesAdd?.cart) {
        debugLog('addToCart successful')
        setCart(data.cartLinesAdd.cart)
        setDidJustAddToCart(lineItemsToAdd[0].merchandiseId)
        callback && callback(null, data.cartLinesAdd.cart)
      }
    } catch (error) {
      debugError('addToCart error', error)
      callback && callback(error)
    } finally {
      setLoading(false)
    }
  }

  // ----------------------------
  // Update cart/checkout info (email updates are typically handled at checkout)
  const updateCartEmail = (email, callback) => {
    // Note: Email updates in the new API are typically handled during checkout process
    // This function is kept for compatibility but may need implementation if required
    debugWarn('updateCartEmail called - may need implementation for new API')
    callback && callback()
  }
  

  // ----------------------------
  // Update quantity
  const updateLineItemQuantity = async (lineItemId, quantity) => {
    if (!cart || !cart.id) {
      debugError('No cart available for updating items')
      return
    }

    debugLog('Updating line item quantity', { lineItemId, quantity, cartId: cart.id })
    setLoading(true)

    const lineItemsToUpdate = [
      { id: lineItemId, quantity: quantity }
    ]

    const operation = `
      mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart {
            ${GRAPHQL_CART_FIELDS}
          }
          userErrors {
            field
            message
          }
        }
      }
    `
    
    try {
      const { data, errors } = await shopifyClient[currency].request(operation, {
        variables: {
          cartId: cart.id,
          lines: lineItemsToUpdate
        }
      })

      if (errors) {
        debugError('updateLineItemQuantity GraphQL errors', errors)
        return
      }

      if (data?.cartLinesUpdate?.userErrors?.length > 0) {
        debugError('updateLineItemQuantity userErrors', data.cartLinesUpdate.userErrors)
        return
      }

      if (data?.cartLinesUpdate?.cart) {
        debugLog('updateLineItemQuantity successful')
        setCart(data.cartLinesUpdate.cart)
      }
    } catch (error) {
      debugError('updateLineItemQuantity error', error)
    } finally {
      setLoading(false)
    }
  }

  // ----------------------------
  // Remove item
  const removeLineItem = async (lineItemId) => {
    if (!cart || !cart.id) {
      debugError('No cart available for removing items')
      return
    }

    debugLog('Removing line item', { lineItemId, cartId: cart.id })
    setLoading(true)

    const lineItemIdsToRemove = [lineItemId]

    const operation = `
      mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart {
            ${GRAPHQL_CART_FIELDS}
          }
          userErrors {
            field
            message
          }
        }
      }
    `
    
    try {
      const { data, errors } = await shopifyClient[currency].request(operation, {
        variables: {
          cartId: cart.id,
          lineIds: lineItemIdsToRemove
        }
      })

      if (errors) {
        debugError('removeLineItem GraphQL errors', errors)
        return
      }

      if (data?.cartLinesRemove?.userErrors?.length > 0) {
        debugError('removeLineItem userErrors', data.cartLinesRemove.userErrors)
        return
      }

      if (data?.cartLinesRemove?.cart) {
        debugLog('removeLineItem successful')
        setCart(data.cartLinesRemove.cart)
      }
    } catch (error) {
      debugError('removeLineItem error', error)
    } finally {
      setLoading(false)
    }
  }

  // ----------------------------
  // Toggle currency

  const changeCurrency = (newCurrency) => {
    debugLog('Changing currency', { from: currency, to: newCurrency })
    setCurrency(prev => {
      return newCurrency || (prev === 'EUR' ? 'ZAR' : 'EUR')
    })
  }

	// Context provider with updated API structure
  return (
    <CartContext.Provider value={{ 
			cart, // Updated from currentCheckout
			currency, 
			liveProducts,
			didJustAddToCart,
			loading,
			addToCart, 
			updateLineItemQuantity, 
			removeLineItem, 
			changeCurrency,
      updateCartEmail,
      // Legacy support - map cart to currentCheckout for backward compatibility
      currentCheckout: cart
		}}>
      {children}
    </CartContext.Provider>
  )
}

// Other helper hooks

/*
	---------------------------
	Reconcile live products with contentful products

	- liveProducts: array of shopify products
	- contentfulProducts: array of contentful products
	- flags: array of flags to trigger re-render

	const contentfulProducts = [{
		shopifyZar: {
			handle: "the-be-magugu-1",
			variants: []
		},
		shopifyEur: {
			handle: "the-be-magugu-1",
			variants: []
		}
	}]
*/
export function useProductCollection(contentfulProducts=[], flags=[]) {

	const { liveProducts, currency } = useCartContext()
	const [products, setProducts] = useState([])

	useEffect(() => {
		if (liveProducts.length > 0) {
			const key = currency === 'ZAR' ? 'shopifyZar' : 'shopifyEur'
			const availableProducts = contentfulProducts.filter(p => p[key])
			// console.log('availableProducts', availableProducts)
			const resolvedProducts = availableProducts.map((product) => {
				const liveProduct = liveProducts.find((liveProduct) => {
					// console.log(liveProduct.handle === product[key].handle)
					return liveProduct.handle === product[key].handle
				})
				return {
					...product,
					shopifyProduct: liveProduct
				}
			}).filter(Boolean)
			
			setProducts(resolvedProducts)
		}
	}, [liveProducts, contentfulProducts, currency, ...flags])

	return products
}
/* ------------------------ */

export default CheckoutStore
